import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Megaphone, Calendar } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Badge,
  Skeleton,
  cn,
} from '@gu-corp/gu-ui';
import { getActiveCampaigns } from '~/services/joc-club';
import type { CampaignItem } from '~/services/joc-club-admin';
import { getStatusLabel, getTierLevelBadgeLabel } from '~/utils/joc-club-labels';
import { getLocalized } from '~/utils/localized';

const AUTOPLAY_MS = 6000;

// Minimal structural type for the embla carousel API exposed via setApi
interface SliderApi {
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  selectedScrollSnap: () => number;
  scrollSnapList: () => number[];
  on: (event: string, callback: () => void) => void;
}

export const CampaignSlider: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const siteLocale = i18n.language;
  const [items, setItems] = useState<CampaignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [api, setApi] = useState<SliderApi | null>(null);
  const [current, setCurrent] = useState(0);
  const hoveringRef = useRef(false);

  useEffect(() => {
    getActiveCampaigns()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Track selected slide for the dots indicator
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
  }, [api]);

  // Auto-advance, paused while hovering
  useEffect(() => {
    if (!api || items.length <= 1) return;
    const timer = setInterval(() => {
      if (!hoveringRef.current) api.scrollNext();
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [api, items.length]);

  if (loading) {
    return (
      <Skeleton
        data-testid="campaign-slider"
        className="h-56 w-full rounded-2xl md:h-64 lg:h-56.75"
      />
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="campaign-slider"
        className="flex h-56 flex-col items-center justify-center rounded-2xl border border-dashed bg-muted/20 text-center md:h-64 lg:h-56.75"
      >
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <Megaphone className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-base font-medium">{t('joc_club.campaigns_coming_soon')}</p>
      </div>
    );
  }

  return (
    <div
      data-testid="campaign-slider"
      className="group relative"
      onMouseEnter={() => {
        hoveringRef.current = true;
      }}
      onMouseLeave={() => {
        hoveringRef.current = false;
      }}
    >
      <Carousel setApi={setApi as never} opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {items.map((item) => {
            const now = new Date();
            const startAt = item.startAt ? new Date(item.startAt) : null;
            const endAt = item.endAt ? new Date(item.endAt) : null;
            const isActive = startAt && endAt && now >= startAt && now <= endAt;

            return (
              <CarouselItem key={item.id}>
                <div className="relative h-56 overflow-hidden rounded-2xl md:h-64 lg:h-56.75">
                  {/* Background: campaign image, or brand gradient */}
                  {item.imageUrl ? (
                    <>
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/5" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-rose-950" />
                  )}

                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 space-y-2 p-5 md:p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      {isActive && (
                        <Badge className="border-0 bg-white/90 text-[10px] font-bold text-primary shadow-sm">
                          {getStatusLabel(t, 'active')}
                        </Badge>
                      )}
                      <Badge className="border-white/30 bg-white/15 text-[10px] text-white backdrop-blur-sm">
                        {getTierLevelBadgeLabel(t, item.minTier)}
                      </Badge>
                    </div>
                    <h2 className="line-clamp-2 text-xl font-bold leading-snug text-white drop-shadow-sm md:text-2xl">
                      {getLocalized(item.title, siteLocale)}
                    </h2>
                    <p className="line-clamp-2 max-w-2xl text-sm text-white/80">
                      {getLocalized(item.description, siteLocale)}
                    </p>
                    <div className="flex items-center gap-1.5 text-[11px] text-white/70">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {startAt?.toLocaleDateString()} — {endAt?.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {items.length > 1 && (
          <>
            {/* Visible by default (touch devices); fade in on hover/focus for pointer devices */}
            <CarouselPrevious className="left-3 border-0 bg-black/30 text-white backdrop-blur-sm transition-opacity hover:bg-black/50 hover:text-white focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100" />
            <CarouselNext className="right-3 border-0 bg-black/30 text-white backdrop-blur-sm transition-opacity hover:bg-black/50 hover:text-white focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100" />
          </>
        )}
      </Carousel>

      {/* Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-1 left-1/2 flex -translate-x-1/2 items-center">
          {items.map((item, i) => (
            <button
              key={item.id}
              aria-label={getLocalized(item.title, siteLocale) || `${i + 1}`}
              aria-current={i === current}
              onClick={() => api?.scrollTo(i)}
              className="flex h-7 items-center px-1"
            >
              <span
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === current ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/75',
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignSlider;
